import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Pagination,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Breadcrumbs,
  Link,
  Grid,
  Avatar,
  CardHeader,
  Paper,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  Search,
  Refresh,
  Add,
  FilterList,
  Visibility,
  Home,
  ListAlt,
  CleaningServices,
  Schedule,
  Person,
  PriorityHigh,
  TrendingUp,
  Assignment
} from '@mui/icons-material';
import { listTasks, getTaskStatistics } from '../../services/api';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

export const TaskList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();

  const [tasks, setTasks] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [taskTypeFilter, setTaskTypeFilter] = useState('all');
  const [totalTasks, setTotalTasks] = useState(0);
  const [stats, setStats] = useState(null);

  const statusOptions = [
    { value: 'all', label: 'All Statuses', color: 'default' },
    { value: 'pending', label: '⏳ Pending', color: 'default' },
    { value: 'in_progress', label: '🔄 In Progress', color: 'warning' },
    { value: 'completed', label: '✅ Completed', color: 'success' },
    { value: 'cancelled', label: '❌ Cancelled', color: 'error' },
    { value: 'overdue', label: '🚨 Overdue', color: 'error' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities', color: 'default' },
    { value: 'low', label: '🟢 Low Priority', color: 'success' },
    { value: 'normal', label: '🔵 Normal Priority', color: 'info' },
    { value: 'high', label: '🟡 High Priority', color: 'warning' },
    { value: 'urgent', label: '🔴 Urgent Priority', color: 'error' }
  ];

  const taskTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'cleaning', label: '🧹 Regular Cleaning' },
    { value: 'deep_cleaning', label: '✨ Deep Cleaning' },
    { value: 'disinfection', label: '🧴 Disinfection' },
    { value: 'linen_change', label: '🛏️ Linen Change' },
    { value: 'maintenance', label: '🔧 Maintenance' },
    { value: 'inspection', label: '🔍 Inspection' }
  ];

  const loadTasks = useCallback(async (p = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: p,
        limit: 10,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
        taskType: taskTypeFilter !== 'all' ? taskTypeFilter : undefined,
        search: searchTerm || undefined,
        sortBy: 'dueAt',
        sortOrder: 'asc'
      };

      const res = await listTasks(params);
      
      setTasks(res.data.tasks || []);
      setPages(res.data.pagination?.pages || 1);
      setPage(res.data.pagination?.page || p);
      setTotalTasks(res.data.pagination?.total || 0);
      
    } catch (err) {
      console.error('Load tasks error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load tasks';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, priorityFilter, taskTypeFilter, enqueueSnackbar]);

  // Load tasks when filters change
  useEffect(() => {
    loadTasks(1);
  }, [loadTasks]);

  const handleSearch = () => {
    loadTasks(1);
  };

  const handleRefresh = () => {
    loadTasks(page);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setTaskTypeFilter('all');
    // The useEffect will automatically trigger when these state variables change
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'cancelled': return 'error';
      case 'overdue': return 'error';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'normal': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const isOverdue = (task) => {
    return task.status !== 'completed' && 
           task.status !== 'cancelled' && 
           new Date(task.dueAt) < new Date();
  };

  const getTaskTypeIcon = (taskType) => {
    switch (taskType) {
      case 'cleaning': return '🧹';
      case 'deep_cleaning': return '✨';
      case 'disinfection': return '🧴';
      case 'linen_change': return '🛏️';
      case 'maintenance': return '🔧';
      case 'inspection': return '🔍';
      default: return '📋';
    }
  };

  if (loading && tasks.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        backgroundColor: 'grey.50',
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Tasks...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing your task dashboard
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <ListAlt sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Housekeeping Tasks
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Manage and track all housekeeping operations
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 28 }} />}
                onClick={() => navigate('/hospital/housekeeping-and-linen-management/housekeeping/task-create')}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Create Task
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filters Card */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
              🔍 Filter & Search Tasks
            </Typography>
          }
          subheader={
            <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
              Find specific tasks using filters and search
            </Typography>
          }
          sx={{ pb: 2 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by task number, room, or staff..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 28, color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.1rem',
                    '& input': {
                      padding: '12px 14px',
                      fontSize: '1.1rem'
                    }
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{ fontSize: '1.1rem' }}
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.1rem' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  sx={{ fontSize: '1.1rem' }}
                >
                  {priorityOptions.map(option => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.1rem' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '1.1rem' }}>Task Type</InputLabel>
                <Select
                  value={taskTypeFilter}
                  label="Task Type"
                  onChange={(e) => setTaskTypeFilter(e.target.value)}
                  sx={{ fontSize: '1.1rem' }}
                >
                  {taskTypeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '1.1rem' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title="Clear All Filters">
                  <Button
                    variant="outlined"
                    startIcon={<FilterList sx={{ fontSize: 24 }} />}
                    onClick={handleClearFilters}
                    sx={{ 
                      fontSize: '1.1rem',
                      px: 3,
                      py: 1.5
                    }}
                  >
                    Clear
                  </Button>
                </Tooltip>

                <Tooltip title="Refresh Data">
                  <IconButton 
                    onClick={handleRefresh} 
                    disabled={loading}
                    sx={{ 
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      fontSize: '1.1rem'
                    }}
                  >
                    <Refresh sx={{ fontSize: 28 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Grid>
          </Grid>

          {/* Results Count */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              📊 Showing {tasks.length} of {totalTasks} tasks
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4, 
            fontSize: '1.1rem',
            borderRadius: 3,
            '& .MuiAlert-message': { fontSize: '1.1rem' }
          }}
        >
          {error}
        </Alert>
      )}

      {/* Tasks Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
              📋 Task List
            </Typography>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        
        <CardContent sx={{ p: 0 }}>
          <Table>
            <TableHead sx={{ backgroundColor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                  Task Details
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                  Location
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                  Assigned Staff
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                  Status
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                  Due Date
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tasks.length > 0 ? (
                tasks.map((task) => (
                  <TableRow
                    key={task._id}
                    hover
                    sx={{
                      '&:hover': { 
                        backgroundColor: 'primary.light',
                        transform: 'translateY(-1px)',
                        transition: 'all 0.3s ease'
                      },
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => navigate(`/hospital/housekeeping-and-linen-management/housekeeping/task-detail/${task._id}`)}
                  >
                    <TableCell sx={{ py: 3 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700, mb: 0.5 }}>
                          {getTaskTypeIcon(task.taskType)} {task.taskNumber}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                          <Chip 
                            label={task.taskType?.replace('_', ' ') || 'N/A'} 
                            size="small"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              textTransform: 'capitalize'
                            }}
                          />
                          <Chip 
                            label={task.priority} 
                            color={getPriorityColor(task.priority)}
                            size="small"
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              textTransform: 'capitalize'
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
                          🏨 Room {task.room?.room_no || 'N/A'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                          {task.ward?.name} • {task.floor?.name}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      {task.assignedTo ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 40, height: 40, backgroundColor: 'primary.main' }}>
                            <Person sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {`${task.assignedTo.first_name} ${task.assignedTo.last_name}`}
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                              {task.assignedTo.designation || 'Staff Member'}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Chip 
                          label="👤 Unassigned" 
                          size="medium" 
                          variant="outlined" 
                          color="warning"
                          sx={{ fontSize: '1rem', fontWeight: 600 }}
                        />
                      )}
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={task.status ? task.status.replace('_', ' ') : 'pending'} 
                          color={getStatusColor(task.status)}
                          size="medium"
                          sx={{ 
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'capitalize'
                          }}
                        />
                        {isOverdue(task) && (
                          <Chip 
                            label="🚨 Overdue" 
                            color="error"
                            size="medium"
                            sx={{ fontSize: '1rem', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
                          {task.dueAt ? new Date(task.dueAt).toLocaleDateString() : 'Not set'}
                        </Typography>
                        {task.dueAt && (
                          <Typography variant="body2" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                            ⏰ {new Date(task.dueAt).toLocaleTimeString()}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 3 }}>
                      <Tooltip title="View Task Details">
                        <IconButton
                          size="large"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/hospital/housekeeping-and-linen-management/housekeeping/task-detail/${task._id}`);
                          }}
                          sx={{ 
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': { backgroundColor: 'primary.dark' }
                          }}
                        >
                          <Visibility sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                      <CleaningServices sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                      <Typography variant="h4" sx={{ fontSize: '1.8rem', mb: 1, fontWeight: 600 }}>
                        No Tasks Found
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '1.2rem', mb: 3 }}>
                        {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' 
                          ? 'Try adjusting your search or filters' 
                          : 'Create your first task to get started'
                        }
                      </Typography>
                      <Button 
                        variant="contained" 
                        startIcon={<Add sx={{ fontSize: 28 }} />}
                        onClick={() => navigate('create')}
                        sx={{ 
                          fontSize: '1.2rem',
                          px: 4,
                          py: 1.5,
                          borderRadius: 3
                        }}
                      >
                        Create First Task
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, borderTop: '1px solid', borderColor: 'divider' }}>
              <Pagination
                count={pages}
                page={page}
                onChange={(event, value) => loadTasks(value)}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};